import React, { useState } from 'react';
import { useAuth } from '@/context/auth-context';
import { User, ArrowRight, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const UsernameSetupModal: React.FC = () => {
  const { user, updateUsername } = useAuth();
  const [newUsername, setNewUsername] = useState(user?.username || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!user || !user.needs_username_setup) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername.trim()) {
      setError('Username is required');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await updateUsername(newUsername);
    } catch (err: any) {
      setError(err.message || 'Failed to update username');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-zinc-950/80 backdrop-blur-xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="w-full max-w-md bg-zinc-900 border border-white/10 rounded-[2.5rem] p-10 shadow-2xl shadow-cyan-500/10 relative overflow-hidden group"
        >
          {/* Decorative background glow */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-cyan-500/10 blur-[80px] rounded-full" />
          <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-cyan-500/5 blur-[80px] rounded-full" />

          <div className="relative z-10 text-center mb-10">
            <div className="w-20 h-20 bg-cyan-500/10 border border-cyan-500/20 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-cyan-500/5 group-hover:scale-105 transition-transform duration-500">
              <User className="text-cyan-400 w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold text-white font-display uppercase tracking-tighter mb-3">
              Identify <span className="text-cyan-400">Yourself</span>
            </h2>
            <p className="text-zinc-400 font-medium italic text-sm tracking-tight opacity-80">Pick a name to represent you in the studio.</p>
          </div>

          <form onSubmit={handleSubmit} className="relative z-10 space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-400/60 ml-1">Username Reference</label>
              <div className="relative group/input">
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="w-full px-6 py-4 bg-zinc-950/50 border border-white/10 rounded-2xl text-white font-medium placeholder:text-zinc-700 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500/50 transition-all"
                  placeholder="Enter username..."
                  autoFocus
                />
                <div className="absolute inset-0 rounded-2xl bg-cyan-500/5 opacity-0 group-focus-within/input:opacity-100 transition-opacity pointer-events-none" />
              </div>
            </div>

            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-[10px] font-bold uppercase tracking-widest text-center flex items-center justify-center gap-2"
              >
                <AlertCircle className="w-4 h-4" />
                {error}
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group/btn relative w-full h-16 bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-widest text-[11px] rounded-2xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-[shimmer_1.5s_infinite] transition-transform" />
              <span className="relative z-10 flex items-center gap-2 transition-transform group-hover/btn:scale-105">
                {loading ? 'Initializing...' : (
                  <>
                    Confirm Identity <ArrowRight className="w-5 h-5 transition-transform group-hover/btn:translate-x-1" />
                  </>
                )}
              </span>
            </button>
            <p className="text-[9px] text-zinc-500 text-center uppercase tracking-[0.15em] font-medium opacity-50">This name can be changed later in settings</p>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default UsernameSetupModal;
